import React, { ReactNode, useEffect, useState } from 'react'
//Helpers
import { Col, Container, Row } from 'react-bootstrap'
import useQueryProperty from '@hooks/useQueryProperty'
import {
  getAge,
  getBathrooms,
  getEnvironment,
  getOperations,
  getOrientation,
  getPrices,
  getRooms,
  getSurfaces,
  getType,
} from '@helpers/helper.properties'
import { PriceTokko, RealEstateDataPageComponentData } from 'types'
import Image from '@components/Image'

interface AmenitiItem {
  title: string
  value: string | number | object
  subValue?: string
}

const Property_Amenities_3 = ({
  propertyID,
  image_gatsby,
  global_data, // groupIds,
  contentColorCustom,
  backgroundHexa,
  titleHexa, // groupName,
}: { propertyID: string } & RealEstateDataPageComponentData) => {
  const { data: allPropertyData, isLoading } = useQueryProperty(
    propertyID,
    global_data.pageConfig.keys.tokko,
  )

  const [operationSelected, setOperationSelected] = useState<string>()

  function getLessPrice(price: PriceTokko[]) {
    const current_price = price?.find(
      (operation) =>
        operation.type === operationSelected ||
        (operation.type.includes('temporario') && operationSelected?.toLowerCase() == 'temporario'),
    )
    if (current_price?.is_less_than_previous_price) {
      return `${current_price?.currency} ${current_price?.previous_price}`
    }
    return ''
  }

  function getPriceByOperation(price: PriceTokko[]) {
    const current_price = price?.find(
      (operation) =>
        operation.type === operationSelected ||
        (operation.type.includes('temporario') && operationSelected?.toLowerCase() == 'temporario'),
    )
    return `${current_price?.currency} ${current_price?.price} - ${
      current_price?.period ? current_price?.period : ''
    }`
  }

  const [orderAmenitiesRowGroup, setOrderAmenitiesRowGroup] = useState<AmenitiItem[]>([])

  // Amenities Row Group (data)
  const amenitiesRowGroup: AmenitiItem[] = [
    {
      title: 'Valor',
      subValue: getLessPrice(getPrices(allPropertyData)),
      value: getPriceByOperation(getPrices(allPropertyData)),
    },
    {
      title: 'Antigüedad',
      value: getAge(allPropertyData),
    },
    {
      title: 'Ambientes',
      value: getEnvironment(allPropertyData),
    },
    {
      title: 'Dormitorios',
      value: getRooms(allPropertyData),
    },
    {
      title: 'Baños',
      value: getBathrooms(allPropertyData),
    },
    {
      title: 'm² totales',
      value: getSurfaces(allPropertyData)?.total_surface,
    },
    {
      title: 'm² cubiertas',
      value: getSurfaces(allPropertyData)?.roofed,
    },
    {
      title: 'Orientación',
      value: getOrientation(allPropertyData),
    },
  ]

  // Amenity Item Element | title and value
  const amenitiesItemEl = (name: string, value: string, subValue?: string): ReactNode => (
    <>
      <p
        style={{
          color: contentColorCustom ? titleHexa : '',
          borderColor: contentColorCustom ? titleHexa : '',
        }}
        className="mb-3 pb-2 text-center text-lg-start text-uppercase"
      >
        {name}
      </p>
      {
        <h4
          style={{ color: contentColorCustom ? titleHexa : '' }}
          className={
            'previous-price d-flex align-items-center position-relative ' +
            (subValue ? '' : 'opacity')
          }
        >
          <div>
            {subValue?.split(' ')[0]}{' '}
            {Intl.NumberFormat('de-DE').format(parseInt(subValue?.split(' ')[1] || ''))}
          </div>
          <i
            style={{ color: contentColorCustom ? titleHexa : '' }}
            className="icon-arrow_down"
          ></i>
        </h4>
      }
      <h4
        className="text-uppercase text-center text-lg-start "
        style={{ color: contentColorCustom ? titleHexa : '' }}
      >
        {name === 'Valor'
          ? value.split(' ')[1] == 'Precio'
            ? 'A consultar'
            : value.split(' ')[0] +
              ' ' +
              Intl.NumberFormat('de-DE').format(parseInt(value.split(' ')[1]))
          : value}
      </h4>
      <h5
        style={{ color: contentColorCustom ? titleHexa : '' }}
        className={
          'small period-text text-white ' + (value.split('-')[1]?.length > 3 ? '' : 'opacity')
        }
      >
        {name === 'Valor' ? (value.split('-')[1]?.length > 3 ? value.split('-')[1] : '-') : ''}
      </h5>
    </>
  )

  // Amenity Col Content Element | title and value container
  const colContentEl = (amenitiesItem: ReactNode): ReactNode => (
    <>
      <div className="d-flex flex-column item-amenities">
        <div className="d-flex flex-column">{amenitiesItem}</div>
      </div>
    </>
  )

  // Row Content Amenities Element | column with title and value container
  const amenitiesRowContentEl = (
    title: string,
    value: string | number | object,
    key: number,
    xsCol: number,
    lgCol: number,
    colClass: boolean = false,
    subValue?: string,
  ): ReactNode =>
    value === null || value === 0 || value === undefined ? null : (
      <>
        <Col
          key={key}
          xs={xsCol || 12}
          lg={lgCol}
          style={{ color: contentColorCustom ? titleHexa : '' }}
          className={'text-center text-lg-start' + (colClass ? (!value ? ' empty-block' : '') : '')}
        >
          {value ? colContentEl(amenitiesItemEl(title, `${value}`, subValue)) : null}
        </Col>
      </>
    )

  const amenitiesRowContentElGrid = (
    title: string,
    value: string | number | object,
    key: number,
    xsCol: number,
    lgCol: number,
    colClass: boolean = false,
    subValue?: string,
  ): ReactNode =>
    value === null || value === 0 || value === undefined ? null : (
      <>
        <div
          key={key}
          style={{ color: contentColorCustom ? titleHexa : '' }}
          className={'text-center text-lg-start' + (colClass ? (!value ? ' empty-block' : '') : '')}
        >
          {value ? colContentEl(amenitiesItemEl(title, `${value}`, subValue)) : null}
        </div>
      </>
    )

  useEffect(() => {
    setOperationSelected(getOperations(allPropertyData)[0])
  }, [allPropertyData])

  const orderRows = () => {
    let order = amenitiesRowGroup

    // getEnvironment(allPropertyData) // 2
    // getRooms(allPropertyData) // 3
    // getBathrooms(allPropertyData) // 4
    // getSurfaces(allPropertyData).total_surface // 5
    // getSurfaces(allPropertyData).roofed // 6
    // getOrientation(allPropertyData) // 7

    // m² totales
    // m² cubiertas
    // Orientación

    if (getEnvironment(allPropertyData) === 0) {
      order = order.filter((item) => item.title !== 'Ambientes')
      order.push(amenitiesRowGroup[2])
    }

    if (getRooms(allPropertyData) === 0) {
      order = order.filter((item) => item.title !== 'Dormitorios')
      order.push(amenitiesRowGroup[3])
    }

    if (getBathrooms(allPropertyData) === 0) {
      order = order.filter((item) => item.title !== 'Baños')
      order.push(amenitiesRowGroup[4])
    }

    if (getSurfaces(allPropertyData).total_surface === '0') {
      order = order.filter((item) => item.title !== 'm² totales')
      order.push(amenitiesRowGroup[5])
    }

    if (getSurfaces(allPropertyData).roofed === '0') {
      order = order.filter((item) => item.title !== 'm² cubiertas')
      order.push(amenitiesRowGroup[6])
    }

    if (getOrientation(allPropertyData) === null) {
      order = order.filter((item) => item.title !== 'Orientación')
      order.push(amenitiesRowGroup[7])
    }

    setOrderAmenitiesRowGroup(order)
  }

  return (
    <section
      style={{
        background: contentColorCustom ? backgroundHexa : '',
        color: contentColorCustom ? titleHexa : '',
      }}
      className={
        'property-amenities c ' +
        (isLoading ? ' skeleton ' : '') +
        (image_gatsby ? 'img-section' : 'no-img-section')
      }
    >
      <Container fluid>
        <Row className="justify-content-around align-items-center flex-lg-nowrap flex-lg-row flex-column gap-3">
          {/* Image Col */}
          {image_gatsby ? (
            <Col
              xxl={3}
              className="d-xxl-flex justify-content-center d-none col image-column"
            >
              <Image
                alt="image-prop"
                objectFit="cover"
                image={image_gatsby!}
                className="no-border image-prop"
              />
            </Col>
          ) : null}
          {/* Content Col */}
          <Col
            sm={12}
            md={6}
            xxl={image_gatsby ? 4 : 6}
            className={
              'border-right-color col data-column d-flex flex-column flex-lg-row px-0 px-lg-auto align-items-center ' +
              (image_gatsby ? 'img-section' : 'no-img-section')
            }
          >
            <div
              className="color-line"
              style={{ background: contentColorCustom ? titleHexa : '' }}
            ></div>
            {/* Row operation-type */}
            <Row className="justify-content-center justify-content-lg-around gap-3 w-100 mb-lg-0 mb-5">
              <Col
                xs={6}
                lg={6}
                className="operation-type d-flex justify-content-around flex-lg-column align-items-center align-items-lg-start pe-xxl-3 gap-3 gap-lg-0"
              >
                {getOperations(allPropertyData).map((operation) => (
                  <span
                    style={{
                      color: contentColorCustom ? backgroundHexa : titleHexa,
                      backgroundColor: contentColorCustom ? titleHexa : backgroundHexa,
                    }}
                    className={
                      'd-flex justify-content-center cursor-pointer ' +
                      (operation.toLowerCase() == operationSelected?.toLowerCase() ||
                      (operation.toLowerCase().includes('temporario') &&
                        operationSelected?.toLowerCase()?.includes('temporario'))
                        ? ' active '
                        : '')
                    }
                    onClick={() => setOperationSelected(operation)}
                  >
                    {operation}
                  </span>
                ))}
                <span
                  style={{
                    color: contentColorCustom ? titleHexa : '',
                    borderColor: contentColorCustom ? titleHexa : '',
                  }}
                  className="d-flex justify-content-center type"
                >
                  {getType(allPropertyData)}
                </span>
              </Col>
            </Row>
            {/* Row values-type */}
            <Row className="justify-content-center justify-content-lg-around gap-lg-3 gap-0 row-gap-3 w-100">
              {amenitiesRowGroup
                ?.slice(0, 2) // first 2 items
                ?.map(({ title, value, subValue }, key) =>
                  amenitiesRowContentEl(title, value, key, 6, 8, false, subValue),
                )}
            </Row>
          </Col>
          {/* Amenities Col */}
          <Col
            sm={12}
            md={6}
            xxl={image_gatsby ? 5 : 6}
            className="d-flex justify-content-center col amenities-column px-0"
          >
            {/* Amenities Row Content */}
            <Row className="grid-element justify-content-center justify-content-lg-around gap-lg-3 gap-0 row-gap-3 w-100">
              {amenitiesRowGroup
                ?.slice(2, amenitiesRowGroup?.length) // from 2nd to last
                ?.map(({ title, value, subValue }, key) =>
                  amenitiesRowContentElGrid(title, value, key, 6, 3, true, subValue),
                )}
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  )
}

export default Property_Amenities_3
